import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../config";

const baseUrl = BASE_URL + "projects";

// 📌 Create Project (POST)
export const createProject = createAsyncThunk(
  "project/create",
  async (projectData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${baseUrl}/create`, projectData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error creating project");
    }
  }
);

// 📌 Update Project (PUT)
export const updateProject = createAsyncThunk(
  "project/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${baseUrl}/update/${id}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error updating project");
    }
  }
);

// 📌 Get Project by ID (GET)
export const getProjectById = createAsyncThunk(
  "project/getById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${baseUrl}/get-by-id/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching project");
    }
  }
);

// 📌 Get All Projects (GET)
export const getAllProjects = createAsyncThunk(
  "project/getAll",
  async ({ limit = 10, offset = 0 }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/get-all?limit=${limit}&offset=${offset}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching projects");
    }
  }
);

// 📌 Delete Project (DELETE)
export const deleteProject = createAsyncThunk(
  "project/delete",
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${baseUrl}/delete/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error deleting project");
    }
  }
);

// 🛠 Project Slice
const projectSlice = createSlice({
  name: "project",
  initialState: {
    projects: [],
    project: null,
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createProject.pending, (state) => {
        state.loading = true;
      })
      .addCase(createProject.fulfilled, (state, action) => {
        state.loading = false;
        state.projects.push(action.payload);
      })
      .addCase(createProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })
      .addCase(updateProject.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateProject.fulfilled, (state, action) => {
        state.loading = false;
        state.projects = state.projects.map((project) =>
          project.id === action.payload.id ? action.payload : project
        );
      })
      .addCase(updateProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })
      .addCase(getProjectById.pending, (state) => {
        state.loading = true;
      })
      .addCase(getProjectById.fulfilled, (state, action) => {
        state.loading = false;
        state.project = action.payload;
      })
      .addCase(getProjectById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })
      .addCase(getAllProjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllProjects.fulfilled, (state, action) => {
        state.loading = false;
        state.projects = action.payload.results;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(getAllProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })
      .addCase(deleteProject.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteProject.fulfilled, (state, action) => {
        state.loading = false;
        state.projects = state.projects.filter(
          (project) => project.id !== action.payload
        );
      })
      .addCase(deleteProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      });
  },
});

export default projectSlice.reducer;
